import { BottomSheet } from '@s4l/ds'
import { PriceDetails } from '@s4l/uikit'
import cn from 'classnames'
import PriceDetailsFactory, { type PriceDataType } from 'client/components/PriceDetailsFactory'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { DiscountBadge } from 'src/comps/Atom/Badges/Discount'
import CircularProgress from 'src/comps/Atom/CircularProgress'
import InfoButton from '../InfoButton'
import BeforePrice from './BeforePrice'
import Nights from './Nights'
import PricePerNight from './PricePerNight'
import styles from './styles.module.css'
import TotalPrice from './TotalPrice'
import { useBusiness } from 'src/hooks/useBusiness'
import { AFFILIATE_TRIVAGO } from 'src/constants/affiliates'
import useExperiment from 'src/hooks/useExperimentConfig'
import { EXPERIMENTS_CONFIG } from 'src/constants/experiments'

const PRICE_DETAILS_EXPERIMENT_KEY = 'WEB-5370'
const isPriceDetailsExperimentActive = Boolean(
  EXPERIMENTS_CONFIG[PRICE_DETAILS_EXPERIMENT_KEY]?.active
)

export enum FeaturedPrice {
  Total = 'total',
  PerNight = 'perNight',
}
export interface IPriceLayout {
  className?: string
  currency: string
  discount?: number
  nights?: number
  priceFinal?: number | null
  priceInitial?: number
  pricePerNight?: number | null
  vats?: React.ReactNode
  priceData?: PriceDataType
  prime?: boolean
  showDiscount?: boolean
  showDiscountBadge?: boolean
  showFrom?: boolean
  featuredPrice?: FeaturedPrice
  loading?: boolean
  showSubtitle?: boolean
  taxDetails?: Array<{
    amount: number
    currency: string
    client: {
      amount: number
      currency: string
    }
    included: boolean
    name: string
  }>
  additionalFees?: Array<{
    amount: number
    currency: string
    local?: {
      amount: number
      currency: string
    }
  }>
  insuranceSelected?: boolean
}

const PerNightPriceComp = ({
  featured = false,
  pricePerNight,
  currency,
}: {
  featured?: boolean
  pricePerNight?: number | null
  currency: string
}) =>
  pricePerNight ? (
    <PricePerNight
      className={cn({ [styles.featuredPricePerNight]: featured })}
      pricePerNight={pricePerNight}
      currency={currency}
    />
  ) : null

const TotalPriceComp = ({
  featured = false,
  priceInitial,
  priceFinal,
  currency,
  showDiscount,
}: {
  featured?: boolean
  priceInitial?: number
  priceFinal?: number | null
  currency: string
  showDiscount: boolean
}) => (
  <>
    {showDiscount && priceInitial && priceFinal && priceFinal < priceInitial ? (
      <BeforePrice
        className={cn({ [styles.featuredBeforePrice]: featured })}
        beforePrice={priceInitial}
        currency={currency}
      />
    ) : null}
    {priceFinal ? (
      <TotalPrice
        className={cn({ [styles.featuredPrice]: featured })}
        totalPrice={priceFinal}
        currency={currency}
        title={false}
      />
    ) : null}
  </>
)

const PriceLayout = ({
  className,
  currency,
  discount,
  pricePerNight,
  priceInitial,
  priceFinal,
  vats,
  priceData,
  nights = 1,
  prime = false,
  showDiscount = true,
  showDiscountBadge = true,
  showFrom = false,
  showSubtitle = true,
  featuredPrice = FeaturedPrice.PerNight,
  loading = false,
  additionalFees,
  taxDetails,
  insuranceSelected,
}: IPriceLayout) => {
  const intl = useIntl()

  // Check for feature flag in URL and persist it
  const [hasNewPriceDetailsFeatureFlag] = useState(() => {
    if (typeof window === 'undefined') return false
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('feature_flag') === 'new-price-details'
  })

  const { affiliate } = useBusiness()
  const { variant } = useExperiment(PRICE_DETAILS_EXPERIMENT_KEY)

  const renderPriceComponent = () => {
    if (!priceData || loading) return null

    const shouldRenderInlinePriceDetails =
      hasNewPriceDetailsFeatureFlag ||
      variant === 1 ||
      (isPriceDetailsExperimentActive && affiliate === AFFILIATE_TRIVAGO)

    if (shouldRenderInlinePriceDetails) {
      const rooms = [{ taxDetails, additionalFees, insuranceSelected, ...priceData }]
      return <PriceDetails rooms={rooms} nights={nights} />
    }

    return (
      <BottomSheet>
        <BottomSheet.Trigger className={styles.trigger}>
          <InfoButton />
        </BottomSheet.Trigger>
        <BottomSheet.Content
          modalOnDesktop
          size="s"
          title={intl.formatMessage({ id: 'global.price-breakdown' })}
        >
          <PriceDetailsFactory {...priceData} variant="simple" isVoucher={false} />
        </BottomSheet.Content>
      </BottomSheet>
    )
  }

  return (
    <>
      <div className={`${styles.priceLayout} ${className}`}>
        <div className={cn(styles.priceRowTotal, { [styles.priceRowTotalFrom]: showFrom })}>
          {showDiscountBadge && discount ? (
            <DiscountBadge discount={discount} isPrime={prime} />
          ) : null}
          {showFrom ? `${intl.formatMessage({ id: 'global.from' })}:` : null}
          <div className={styles.priceRow} data-testid="featured-price">
            {featuredPrice === FeaturedPrice.Total ? (
              <TotalPriceComp
                featured
                currency={currency}
                priceInitial={priceInitial}
                priceFinal={priceFinal}
                showDiscount={showDiscount}
              />
            ) : (
              <PerNightPriceComp pricePerNight={pricePerNight} currency={currency} featured />
            )}
            {loading ? <CircularProgress data-testid="circular-progress" size="s" /> : null}
            {renderPriceComponent()}
          </div>
        </div>
        {showSubtitle && (
          <div className={styles.priceRow}>
            {featuredPrice === FeaturedPrice.Total ? (
              <PerNightPriceComp pricePerNight={pricePerNight} currency={currency} />
            ) : (
              <TotalPriceComp
                currency={currency}
                priceInitial={priceInitial}
                priceFinal={priceFinal}
                showDiscount={showDiscount}
              />
            )}
            {nights ? <Nights nights={nights} /> : null}
          </div>
        )}

        {vats && priceFinal ? <div className={styles.vats}>{vats}</div> : null}
      </div>
    </>
  )
}

export default PriceLayout
